export type AppRouteId =
  | 'home'
  | 'solarAssessment'
  | 'installerChecklist'
  | 'mySolarPlan'
  | 'calculatorDirectory'
  | 'calculatorCategory'
  | 'electricityHub'
  | 'electricityCalculator'
  | 'solarHub'
  | 'solarCalculator'
  | 'solarCompare'
  | 'quoteCompare'
  | 'solarCompanies'
  | 'installerCompany'
  | 'installerProvince'
  | 'installerCity'
  | 'guides'
  | 'guide'
  | 'caseStudies'
  | 'caseStudy'
  | 'about'
  | 'methodology'
  | 'privacy'
  | 'terms'
  | 'disclaimer'
  | 'contact'
  | 'advertise'
  | 'corrections'
  | 'sources'
  | 'topicCluster'
  | 'solarFaq'
  | 'solarService'
  | 'seoLanding';

export type AppRoutePattern = {
  id: AppRouteId;
  path?: string;
  index?: true;
};

/**
 * The single route-path registry used by the browser router, static renderer,
 * prerender validator, and sitemap coverage checks.
 */
export const appRoutePatterns: readonly AppRoutePattern[] = [
  { id: 'home', index: true },
  { id: 'solarAssessment', path: 'solar-assessment' },
  { id: 'installerChecklist', path: 'installer-checklist' },
  { id: 'mySolarPlan', path: 'my-solar-plan' },
  { id: 'mySolarPlan', path: 'my-solar-plan/quotes' },
  { id: 'mySolarPlan', path: 'my-solar-plan/backup' },
  { id: 'mySolarPlan', path: 'my-solar-plan/scenarios' },
  { id: 'mySolarPlan', path: 'my-solar-plan/daily-use' },
  { id: 'mySolarPlan', path: 'my-solar-plan/roof' },
  { id: 'mySolarPlan', path: 'my-solar-plan/installers' },
  { id: 'mySolarPlan', path: 'my-solar-plan/project-brief' },
  { id: 'mySolarPlan', path: 'my-solar-plan/quote-checks' },
  { id: 'mySolarPlan', path: 'my-solar-plan/bill-history' },
  { id: 'calculatorDirectory', path: 'calculators' },
  { id: 'calculatorCategory', path: 'calculators/:category' },
  { id: 'electricityHub', path: 'electricity' },
  { id: 'electricityCalculator', path: 'electricity/calculators/:calculatorId' },
  { id: 'solarHub', path: 'solar' },
  { id: 'solarCalculator', path: 'solar/calculators/:calculatorId' },
  { id: 'solarCompare', path: 'solar/compare' },
  { id: 'quoteCompare', path: 'solar/compare-quotes' },
  { id: 'solarCompanies', path: 'solar-companies' },
  { id: 'installerCompany', path: 'solar/installers/company/:slug' },
  { id: 'installerProvince', path: 'solar/installers/:province' },
  { id: 'installerCity', path: 'solar/installers/:province/:city' },
  { id: 'guides', path: 'guides' },
  { id: 'guide', path: 'guides/:slug' },
  { id: 'caseStudies', path: 'case-studies' },
  { id: 'caseStudy', path: 'case-studies/:slug' },
  { id: 'about', path: 'about' },
  { id: 'methodology', path: 'methodology' },
  { id: 'privacy', path: 'privacy' },
  { id: 'terms', path: 'terms' },
  { id: 'disclaimer', path: 'disclaimer' },
  { id: 'contact', path: 'contact' },
  { id: 'advertise', path: 'advertise' },
  { id: 'corrections', path: 'corrections' },
  { id: 'sources', path: 'sources' },
  { id: 'topicCluster', path: 'learn/solar-cost-savings' },
  { id: 'topicCluster', path: 'learn/system-sizing' },
  { id: 'topicCluster', path: 'learn/battery-brownouts' },
  { id: 'topicCluster', path: 'learn/installer-selection' },
  { id: 'topicCluster', path: 'learn/net-metering-regulations' },
  { id: 'topicCluster', path: 'learn/electricity-education' },
  { id: 'solarFaq', path: 'solar-faq-philippines' },
  { id: 'solarService', path: 'solar-decision-center-philippines' },
  { id: 'solarService', path: 'solar-quote-checker-philippines' },
  { id: 'solarService', path: 'philippine-solar-data-explorer' },
  { id: 'seoLanding', path: 'solar-calculator-philippines' },
  { id: 'seoLanding', path: 'solar-installer-cavite' },
  { id: 'seoLanding', path: 'solar-installer-calabarzon' },
  { id: 'seoLanding', path: 'solar-for-5000-monthly-electricity-bill' },
  { id: 'seoLanding', path: 'solar-for-10000-monthly-electricity-bill' },
  { id: 'seoLanding', path: 'hybrid-solar-system-philippines' },
  { id: 'seoLanding', path: 'solar-during-brownouts-philippines' },
  { id: 'seoLanding', path: 'solar-battery-cost-philippines' },
] as const;

export const redirectRoutePatterns = [
  { path: 'solar/installers', to: '/solar-companies' },
  { path: 'solar/request-quotes', to: '/solar-companies' },
  { path: 'solar-panel-cost-philippines', to: '/guides/solar-panel-cost-philippines' },
] as const;

export const blockedRoutePaths = ['solar/installers/join'] as const;

export function routePatternMatches(pattern: AppRoutePattern, pathname: string) {
  if (blockedRoutePaths.some((path) => pathname === `/${path}` || pathname === `/${path}/`)) {
    return false;
  }
  if (pattern.index) return pathname === '/';
  const source = String(pattern.path)
    .split('/')
    .map((segment) =>
      segment.startsWith(':') ? '[^/]+' : segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    )
    .join('/');
  return new RegExp(`^/${source}/?$`).test(pathname);
}

export function hasAppRoute(pathname: string) {
  return appRoutePatterns.some((pattern) => routePatternMatches(pattern, pathname));
}
